import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

/**
 * Composant de header unifié pour les pages Services, Produits et À propos
 * @param {Object} props - Les propriétés du composant
 * @param {string} props.pageType - Type de page ('services', 'products', 'about')
 * @param {Array} props.tags - Tags à afficher (pour Services et Produits)
 */
const EnhancedPageHeader = ({ pageType = 'about', tags = [] }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Définition des contenus spécifiques à chaque page
  const pageContent = {
    services: {
      title: language === 'fr' ? 'Nos services' : language === 'ar' ? 'خدماتنا' : 'Our Services',
      description: language === 'fr' 
        ? 'Découvrez notre gamme complète de services techniques spécialisés pour l\'industrie et le secteur Pétrole & Gaz'
        : language === 'ar' 
        ? 'اكتشف مجموعتنا الكاملة من الخدمات الفنية المتخصصة للصناعة وقطاع النفط والغاز'
        : 'Discover our complete range of specialized technical services for industry and the Oil & Gas sector',
      backgroundImage: 'none',
      icon: (
        <div className="service-container" style={{
          position: 'relative',
          width: '250px',
          height: '250px'
        }}>
          <div className="service-circle" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 107, 0, 0.3) 0%, rgba(255, 107, 0, 0) 70%)',
            animation: 'pulse 3s infinite'
          }}></div>
          
          <div className="service-inner" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 107, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '700',
            fontSize: '1.25rem',
            boxShadow: '0 0 30px rgba(255, 107, 0, 0.3)'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '60px', height: '60px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          
          <div className="service-small" style={{
            position: 'absolute',
            top: '20%',
            right: '10%',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.2)',
            animation: 'float 4s ease-in-out infinite'
          }}></div>
          
          <div className="service-small" style={{
            position: 'absolute',
            bottom: '20%',
            left: '10%',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.2)',
            animation: 'float 3s ease-in-out infinite 1s'
          }}></div>
        </div>
      ),
      animation: `
        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
            opacity: 0.5;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.3;
          }
        }
        
        @keyframes float {
          0% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
          100% {
            transform: translateY(0);
          }
        }
      `
    },
    products: {
      title: t('products.title'),
      description: language === 'fr' 
        ? 'Découvrez notre gamme complète de produits d\'instrumentation de haute qualité pour le secteur Pétrole, Gaz et Industrie.'
        : language === 'ar' 
        ? 'اكتشف مجموعتنا الكاملة من منتجات القياس عالية الجودة لقطاع النفط والغاز والصناعة.'
        : 'Discover our complete range of high-quality instrumentation products for the Oil, Gas and Industry sector.',
      backgroundImage: 'none',
      icon: (
        <div className="product-container" style={{
          position: 'relative',
          width: '250px',
          height: '250px'
        }}>
          <div className="product-circle" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 107, 0, 0.3) 0%, rgba(255, 107, 0, 0) 70%)',
            animation: 'pulse 3s infinite'
          }}></div>
          
          <div className="product-inner" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 107, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '700',
            fontSize: '1.25rem',
            boxShadow: '0 0 30px rgba(255, 107, 0, 0.3)'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '60px', height: '60px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
          
          <div className="product-small" style={{
            position: 'absolute',
            top: '20%',
            right: '10%',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.2)',
            animation: 'float 4s ease-in-out infinite'
          }}></div>
          
          <div className="product-small" style={{
            position: 'absolute',
            bottom: '20%',
            left: '10%',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.2)',
            animation: 'float 3s ease-in-out infinite 1s'
          }}></div>
        </div>
      ),
      animation: `
        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
            opacity: 0.5;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.3;
          }
        }
        
        @keyframes float {
          0% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
          100% {
            transform: translateY(0);
          }
        }
      `
    },
    about: {
      title: t('about.title'),
      description: language === 'fr' 
        ? 'Découvrez notre histoire, notre expertise et notre vision pour l\'avenir de l\'instrumentation Pétrole, Gaz et Industrie.'
        : language === 'ar' 
        ? 'اكتشف قصتنا وخبرتنا ورؤيتنا لمستقبل أجهزة القياس البتروكيماوية.'
        : 'Discover our story, our expertise, and our vision for the future of petrochemical instrumentation.',
      backgroundImage: 'none',
      icon: (
        <div className="about-container" style={{
          position: 'relative',
          width: '250px',
          height: '250px'
        }}>
          <div className="about-circle" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 107, 0, 0.3) 0%, rgba(255, 107, 0, 0) 70%)',
            animation: 'pulse 3s infinite'
          }}></div>
          
          <div className="about-inner" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 107, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '700',
            fontSize: '1.25rem',
            boxShadow: '0 0 30px rgba(255, 107, 0, 0.3)'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '60px', height: '60px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          
          <div className="about-small" style={{
            position: 'absolute',
            top: '20%',
            right: '10%',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.2)',
            animation: 'float 4s ease-in-out infinite'
          }}></div>
          
          <div className="about-small" style={{
            position: 'absolute',
            bottom: '20%',
            left: '10%',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.2)',
            animation: 'float 3s ease-in-out infinite 1s'
          }}></div>
        </div>
      ),
      animation: `
        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
            opacity: 0.5;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.3;
          }
        }
        
        @keyframes float {
          0% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
          100% {
            transform: translateY(0);
          }
        }
      `
    }
  };

  // Sélectionner le contenu en fonction du type de page
  const content = pageContent[pageType] || pageContent.about;

  // Statistiques pour la page À propos
  const stats = [
    {
      value: '20+',
      label: language === 'fr' ? 'Années d\'Expérience' : language === 'ar' ? 'سنوات من الخبرة' : 'Years of Experience'
    },
    {
      value: '100%',
      label: language === 'fr' ? 'Équipe Certifiée' : language === 'ar' ? 'فريق معتمد' : 'Certified Team'
    },
    {
      value: '24/7',
      label: language === 'fr' ? 'Support Client' : language === 'ar' ? 'دعم العملاء' : 'Customer Support'
    }
  ];

  return (
    <section className="section enhanced-header" style={{
      backgroundColor: '#1a1a1a',
      color: 'white',
      paddingTop: '10rem',
      paddingBottom: '8rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div className="blob" style={{ top: '-20%', right: '-10%', opacity: 0.2 }}></div>
      <div className="blob" style={{ bottom: '-30%', left: '-15%', opacity: 0.2 }}></div>
      <div className="bg-pattern"></div>
      
      {/* Background decoration */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '50%',
        opacity: 0.1,
        zIndex: 1,
        backgroundImage: content.backgroundImage,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom center',
        backgroundSize: 'contain'
      }}></div>
      
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(12, 1fr)', gap: '2rem' }}>
          <div className="col-span-8 md-col-span-12">
            <h1 className="hero-title enhanced-header-title fade-in" style={{ 
              fontSize: '3.5rem',
              marginBottom: '1rem',
              background: 'linear-gradient(90deg, #ffffff, #FF9A40)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textFillColor: 'transparent'
            }}>
              {content.title}
            </h1>
            
            <p className="fade-in enhanced-header-subtitle" style={{ 
              fontSize: '1.25rem',
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '800px',
              marginBottom: '2rem'
            }}>
              {content.description}
            </p>
            
            {/* Tags pour Services, Produits et À propos */}
            {tags && tags.length > 0 && (
              <div className={`${pageType}-tags fade-in`} style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                marginTop: '2rem'
              }}>
                {tags.map((tag, index) => (
                  <div 
                    key={index}
                    style={{ 
                      backgroundColor: 'rgba(255, 107, 0, 0.2)',
                      color: 'white',
                      padding: '0.5rem 1.5rem',
                      borderRadius: 'var(--radius-full)',
                      fontSize: '0.9375rem',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {tag.icon}
                    {tag.label}
                  </div>
                ))}
              </div>
            )}
            
            {/* Statistiques désactivées */}
            {false && (
              <div className="stats-row fade-in" style={{
                display: 'flex',
                gap: '2.5rem',
                flexWrap: 'wrap',
                marginTop: '3rem'
              }}>
                {stats.map((stat, index) => (
                  <div key={index} className="stat-item">
                    <div style={{ 
                      fontSize: '3rem', 
                      fontWeight: '800', 
                      color: 'var(--assco-orange)',
                      lineHeight: '1'
                    }}>
                      {stat.value}
                    </div>
                    <div style={{ 
                      color: 'white', 
                      fontSize: '1.125rem',
                      marginTop: '0.5rem'
                    }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="col-span-4 md-col-span-12 fade-in" style={{ animationDelay: '0.3s' }}>
            <div style={{
              position: 'relative',
              height: '100%',
              minHeight: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {/* Icon spécifique à chaque page */}
              {content.icon}
            </div>
          </div>
        </div>
      </div>
      
      {/* Shape Divider - Ultra Smooth Mobile Wave */}
      <div className="shape-divider" style={{ transform: 'rotate(180deg)' }}>
        <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <defs>
            <filter id="smooth-pages" colorInterpolationFilters="sRGB">
              <feGaussianBlur in="SourceGraphic" stdDeviation="0.5"/>
            </filter>
          </defs>
          <path d="M0,0V46.29c47.79,22.4,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" className="shape-fill" style={{filter: 'url(#smooth-pages)'}}></path>
        </svg>
      </div>
      
      <style jsx>{`
        ${content.animation}
      `}</style>
    </section>
  );
};

export default EnhancedPageHeader;